import type { SavedObjectiveRules } from 'definitions';

/**
 * Identity and fingerprint for an experiment's objectives, shared by the dashboard and the API
 * so "edited since load" means the same thing on both sides.
 *
 * Works on the experiment's own columns, not on resolveObjectives: a null primary is "the
 * website's default" and has to stay distinguishable from a primary that happens to be the same
 * event today. Secondaries keep their order, because it is the order the report shows them in,
 * so reordering counts as an edit.
 */

export const EMPTY_OBJECTIVES: SavedObjectiveRules = { primaryGoal: null, secondaryGoals: [] };

type ExperimentObjectiveColumns = { primaryGoal?: string | null; secondaryGoals?: unknown };

const asName = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * An experiment's two objective columns in the saved shape: names trimmed, blanks and non-strings
 * dropped, repeats removed (first one wins). Not length-checked: the server runs the result
 * through validateSavedObjectives, and the dashboard only compares and displays it.
 */
export function objectivesFromExperiment(experiment: ExperimentObjectiveColumns): SavedObjectiveRules {
  const listed = Array.isArray(experiment.secondaryGoals) ? experiment.secondaryGoals : [];
  const secondaryGoals = listed.map(asName).filter((name): name is string => name !== null);
  return { primaryGoal: asName(experiment.primaryGoal), secondaryGoals: [...new Set(secondaryGoals)] };
}

/**
 * Objectives are fixed while an experiment runs. The runtime bundle only forwards the events its
 * running experiments are measured on, and it is rebuilt at start, not on every goal edit, so a
 * goal switched mid-run would go uncollected; and a verdict computed on an event that was only
 * measured for part of the run is not one result. To measure something else, start a new
 * experiment. Shared so the API refuses exactly what the card disables.
 */
export const objectivesLocked = (status: string | null | undefined): boolean => status === 'Running';

export const OBJECTIVES_LOCKED_MESSAGE =
  "Objectives can't change while the experiment is running. Duplicate it and start the copy to measure something else.";

/**
 * Every column resolveObjectives reads. `eventGoals` is the legacy list: its first entry is still
 * the primary when `primaryGoal` is null, so writing it can change what an experiment measures.
 */
const GOAL_COLUMNS = ['primaryGoal', 'secondaryGoals', 'eventGoals'] as const;

type GoalColumns = Partial<Record<(typeof GOAL_COLUMNS)[number], unknown>>;

type ChangesObjectivesParams = { before: GoalColumns; after: Readonly<Record<string, unknown>> };

/**
 * True when writing `after` over `before` would change any column the objectives come from.
 *
 * Compares the raw values the write would store, not normalised or hashed objectives: the lock
 * guards what is written, and a write is stored as sent. A normalised comparison let `123`, a
 * padded name, a repeat or a crafted string that hashed the same as the stored goals pass as
 * "unchanged" and land on a running experiment. Strict is the safe direction here: resending the
 * stored values exactly still passes, and anything else is a change.
 */
export function changesObjectives({ before, after }: ChangesObjectivesParams): boolean {
  return GOAL_COLUMNS.some(
    (column) => column in after && JSON.stringify(after[column] ?? null) !== JSON.stringify(before[column] ?? null),
  );
}

export const isEmptyObjectives = ({ primaryGoal, secondaryGoals }: SavedObjectiveRules): boolean =>
  primaryGoal === null && secondaryGoals.length === 0;

/**
 * A short fingerprint of an objectives set: FNV-1a over the primary (or an empty marker for the
 * website default) and the secondaries in order. Stored on the experiment when saved objectives
 * are loaded, then compared with the live columns to tell whether they were edited since.
 * Synchronous, so the dashboard can recompute it on every render; collisions only ever mean an
 * edit goes unflagged.
 */
export function objectivesHash({ primaryGoal, secondaryGoals }: SavedObjectiveRules): string {
  const text = [
    `primary:${primaryGoal === null ? '' : primaryGoal.trim()}`,
    ...secondaryGoals.map((name) => `secondary:${name.trim()}`),
  ].join('\n');
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export const isSameObjectives = (a: SavedObjectiveRules, b: SavedObjectiveRules): boolean =>
  objectivesHash(a) === objectivesHash(b);

export type ObjectiveCondition = { role: 'primary' | 'secondary'; key: string; label: string };

/**
 * What a load changes. `reordered` is true when the same secondaries come in another order: the
 * diff then has no adds or removes, but the report's order changes and the hash differs.
 */
export type ObjectivesDiff = {
  adds: ObjectiveCondition[];
  removes: ObjectiveCondition[];
  keeps: ObjectiveCondition[];
  reordered: boolean;
};

/** The label the card uses for a null primary. */
export const WEBSITE_DEFAULT_LABEL = 'Website default';

/** The primary, then each secondary in order, as chips. A null primary is the website default. */
export function flattenObjectives({ primaryGoal, secondaryGoals }: SavedObjectiveRules): ObjectiveCondition[] {
  const primary: ObjectiveCondition = {
    role: 'primary',
    key: `primary:${primaryGoal ?? ''}`,
    label: primaryGoal ?? WEBSITE_DEFAULT_LABEL,
  };
  return [
    primary,
    ...secondaryGoals.map((name) => ({ role: 'secondary' as const, key: `secondary:${name}`, label: name })),
  ];
}

/** What changes going from `current` to `next`. A condition in both is a keep. */
export function diffObjectives(current: SavedObjectiveRules, next: SavedObjectiveRules): ObjectivesDiff {
  const currentConditions = flattenObjectives(current);
  const nextConditions = flattenObjectives(next);
  const currentKeys = new Set(currentConditions.map(({ key }) => key));
  const nextKeys = new Set(nextConditions.map(({ key }) => key));
  const adds = nextConditions.filter(({ key }) => !currentKeys.has(key));
  const removes = currentConditions.filter(({ key }) => !nextKeys.has(key));
  return {
    adds,
    removes,
    keeps: nextConditions.filter(({ key }) => currentKeys.has(key)),
    reordered: adds.length === 0 && removes.length === 0 && !isSameObjectives(current, next),
  };
}
